import * as React from "react";
import { useLazyRef } from "@/shared/hooks/use-lazy-ref";
import { useAsRef } from "@/shared/hooks/use-as-ref";
import type {
  Store,
  StoreState,
  StoreAction,
  FileState,
  FileUploadProps,
} from "./types";
import { ROOT_NAME } from "./constants";

export const StoreContext = React.createContext<Store | null>(null);

export function useStoreContext(consumerName: string): Store {
  const context = React.useContext(StoreContext);
  if (!context) {
    throw new Error(`\`${consumerName}\` must be used within \`${ROOT_NAME}\``);
  }
  return context;
}

export function useStore<T>(selector: (state: StoreState) => T): T {
  const store = useStoreContext("useStore");

  const lastValueRef = useLazyRef<{ value: T; state: StoreState } | null>(
    () => null
  );

  const getSnapshot = React.useCallback(() => {
    const state = store.getState();
    const prevValue = lastValueRef.current;

    const nextValue = selector(state);

    // If state reference changed, always return new value (state was updated)
    // This is the key - when we create a new Map in SET_PROGRESS, state reference changes
    if (!prevValue || prevValue.state !== state) {
      lastValueRef.current = { value: nextValue, state };
      return nextValue;
    }

    // State reference is the same - check if the selected value actually changed
    // For FileState objects, compare progress, status, and error
    if (
      typeof nextValue === "object" &&
      nextValue !== null &&
      !Array.isArray(nextValue) &&
      prevValue.value &&
      typeof prevValue.value === "object" &&
      prevValue.value !== null &&
      !Array.isArray(prevValue.value)
    ) {
      const prev = prevValue.value as Record<string, unknown>;
      const next = nextValue as Record<string, unknown>;

      // For FileState, check if progress, status, or error changed
      if (
        prev.progress !== next.progress ||
        prev.status !== next.status ||
        prev.error !== next.error
      ) {
        lastValueRef.current = { value: nextValue, state };
        return nextValue;
      }
      // No change detected, return cached value
      return prevValue.value;
    }

    // Primitive values or different types - use reference equality
    if (prevValue.value === nextValue) {
      return prevValue.value;
    }

    lastValueRef.current = { value: nextValue, state };
    return nextValue;
  }, [store, selector, lastValueRef]);

  return React.useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

export function createStore(
  files: Map<File, FileState>,
  urlCache: WeakMap<File, string>,
  invalid: boolean,
  propsRef: React.MutableRefObject<{
    onValueChange?: FileUploadProps["onValueChange"];
    onAccept?: FileUploadProps["onAccept"];
    onFileAccept?: FileUploadProps["onFileAccept"];
    onFileReject?: FileUploadProps["onFileReject"];
    onFileValidate?: FileUploadProps["onFileValidate"];
    onUpload?: FileUploadProps["onUpload"];
  }>
): Store {
  const listeners = new Set<() => void>();

  let state: StoreState = {
    files: new Map(files),
    dragOver: false,
    invalid: invalid,
  };

  function reducer(state: StoreState, action: StoreAction): StoreState {
    switch (action.type) {
      case "ADD_FILES": {
        const next = new Map(files);

        for (const file of action.files) {
          next.set(file, {
            id: crypto.randomUUID(),
            file,
            progress: 0,
            status: "idle",
          });
        }

        files.clear();
        for (const [k, v] of next) files.set(k, v);

        propsRef.current.onValueChange?.(
          Array.from(next.values()).map((f) => f.file)
        );

        return { ...state, files: next };
      }

      case "SET_FILES": {
        const newFileSet = new Set(action.files);
        for (const existingFile of files.keys()) {
          if (!newFileSet.has(existingFile)) {
            files.delete(existingFile);
          }
        }

        for (const file of action.files) {
          const existingState = files.get(file);
          if (!existingState) {
            files.set(file, {
              id: crypto.randomUUID(),
              file,
              progress: 0,
              status: "idle",
            });
          }
        }
        return { ...state, files };
      }

      case "SET_PROGRESS": {
        const fileState = files.get(action.file);
        if (!fileState || fileState.status === "success") return state;

        const nextProgress = action.progress;

        files.set(action.file, {
          ...fileState,
          progress: nextProgress,
          status: nextProgress >= 100 ? "success" : "uploading",
        });

        return { ...state, files: new Map(files) };
      }

      case "SET_SUCCESS": {
        const fileState = files.get(action.file);
        if (!fileState) return state;

        const next = new Map(files);
        next.set(action.file, {
          ...fileState,
          progress: 100,
          status: "success",
        });

        return { ...state, files: next };
      }

      case "SET_ERROR": {
        const fileState = files.get(action.file);
        if (!fileState) return state;

        const next = new Map(files);
        next.set(action.file, {
          ...fileState,
          error: action.error,
          status: "error",
        });

        return { ...state, files: next };
      }

      case "REMOVE_FILE": {
        const next = new Map(files);
        next.delete(action.file);

        const cachedUrl = urlCache.get(action.file);
        if (cachedUrl) {
          URL.revokeObjectURL(cachedUrl);
          urlCache.delete(action.file);
        }

        propsRef.current.onValueChange?.(
          Array.from(next.values()).map((f) => f.file)
        );

        return { ...state, files: next };
      }

      case "SET_DRAG_OVER": {
        return { ...state, dragOver: action.dragOver };
      }

      case "SET_INVALID": {
        return { ...state, invalid: action.invalid };
      }

      case "CLEAR": {
        for (const file of files.keys()) {
          const cachedUrl = urlCache.get(file);
          if (cachedUrl) {
            URL.revokeObjectURL(cachedUrl);
            urlCache.delete(file);
          }
        }

        files.clear();
        const next = new Map<File, FileState>();

        propsRef.current.onValueChange?.([]);

        return { ...state, files: next, invalid: false };
      }

      default:
        return state;
    }
  }

  return {
    getState: () => state,
    dispatch: (action) => {
      state = reducer(state, action);
      for (const listener of listeners) {
        listener();
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

